import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from '../../../i18n'
import type { AlderSeries } from './api'
import { seriesLabel } from './api'

interface Props {
  series: AlderSeries[]
  /** Bucket labels that the chart treats as outliers — summed into "Utenfor diagram". */
  outlierLabels?: string[]
}

const fmtCount = (n: number) => n.toLocaleString('nb-NO')
const fmtPct = (n: number) =>
  `${n.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`
const fmtAge = (n: number | null) =>
  n === null
    ? '–'
    : i18n.t('units.aar', {
      ns: 'bruktbil',
      value: n.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
    })

export default function AgeStatsGrid({ series, outlierLabels }: Props) {
  const { t } = useTranslation('bruktbil')
  const outliers = useMemo(() => new Set(outlierLabels ?? []), [outlierLabels])

  const rows = useMemo(
    () =>
      series.map(s => {
        const outsideCount = s.buckets
          .filter(b => outliers.has(b.label))
          .reduce((sum, b) => sum + b.count, 0)
        const outsideShare = s.total > 0 ? (outsideCount / s.total) * 100 : 0
        return { label: seriesLabel(s.merke, s.klasse), total: s.total, stats: s.stats, outsideCount, outsideShare }
      }),
    [series, outliers],
  )

  if (rows.length === 0) return null

  return (
    <div data-testid="age-stats-grid" className="card p-4 overflow-x-auto">
      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('alderStats.title')}</h3>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-gray-200 dark:border-dark-border text-gray-500 dark:text-gray-400">
            <th className="px-3 py-2 text-left font-medium">{t('aldersprofil.grid.serie')}</th>
            <th className="px-3 py-2 text-right font-medium">{t('common.antall')}</th>
            <th className="px-3 py-2 text-right font-medium">{t('aldersprofil.grid.snittalder')}</th>
            <th className="px-3 py-2 text-right font-medium">P25</th>
            <th className="px-3 py-2 text-right font-medium">{t('detaljer.median')}</th>
            <th className="px-3 py-2 text-right font-medium">P75</th>
            <th className="px-3 py-2 text-right font-medium">{t('aldersprofil.grid.utenforDiagram')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr
              key={r.label}
              data-testid="age-stats-row"
              className="border-b border-gray-100 dark:border-dark-border/60"
            >
              <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{r.label === 'Alle' ? t('common.alle') : r.label}</td>
              <td className="px-3 py-2 text-right tabular-nums">{fmtCount(r.total)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{fmtAge(r.stats.avg_age)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{fmtAge(r.stats.p25_age)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{fmtAge(r.stats.median_age)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{fmtAge(r.stats.p75_age)}</td>
              <td className="px-3 py-2 text-right tabular-nums">
                {fmtCount(r.outsideCount)}
                <span className="text-gray-400 ml-1">({fmtPct(r.outsideShare)})</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
